/* Source of truth since 2026-10-03 (ADR 0013): edit this file. The quiz-v1 HTML prototype is history. */
/* lp-quiz boot: the Shopify adapter for the Stocked quiz (qz-port, 2026-09-25).
 *
 * SOURCE FILE. `node clients/stocked/scripts/lp-quiz-sync.mjs` copies it to assets/lp-quiz-boot.js with a
 * generated header; edit it here, never in assets/.
 *
 * The quiz itself is the prototype (clients/stocked/docs/landing-pages/prototypes/quiz-v1/), generated into
 * assets/lp-quiz.js, lp-quiz-core.js and lp-quiz-dish-popup.js by the sync. This file is the only
 * Shopify-specific code, and it changes nothing in the prototype's source. It:
 *   1. reads the section's payload (<script type="application/json" id="lp-quiz-data">): the dishes the
 *      builder lists, from the builder's own collections, with Liquid's availability, price and variant id;
 *   2. sets window.lpQuiz.prices (the pack prices, from the products) BEFORE lp-quiz-core.js runs, because
 *      the sync points the core's PACK_PRICE / ONE_OFF_PACK_PRICE at them;
 *   3. fetches assets/lp-quiz-dishes.json (the research fields, keyed by handle) and merges it by handle
 *      into the globals the prototype reads (DISHES, GALLERY, IMG, DISH_INFO, DISH_INFO_SHARED), dropping
 *      every dish the payload marks unavailable;
 *   4. points the core's builder gate (QuizCore.BUILDER_LISTED) at the payload's titles, in the builder's
 *      order, then loads lp-quiz.js, and lp-quiz-dish-popup.js once the first screen has drawn (OLEO-230);
 *   5. replaces the prototype's three alert() exits (goCheckout, goBuilder, the skip link) with the real
 *      ones, and keeps dishes without research data free of research labels;
 *   6. sends the quiz's PostHog events (OLE-94): quiz_step with the order's record, quiz_screen_left,
 *      quiz_dish_changed, quiz_add_to_basket and quiz_add_to_basket_result, and quiz_route for the session;
 *   7. (OLEO-136, 06-tracking-spec.md T1, T3, T4) keeps the quiz attempt's id (quiz_attempt_id, quiz_attempt_seq) on
 *      every event and for the session (PostHog only, never on the cart), the For you list as shown on quiz_step at
 *      the Box with each dish tap's position, and the reason order and spice levels on quiz_step;
 *   9. (OLEO-147) asks Skio for the box's allowed dishes when the quiz loads, the same get-dynamic-box read the
 *      builder's productCompatibilityCheck makes and lp-quiz-cart.js repeats at the tap, and hides every listed
 *      dish whose variant Skio does not list, so the quiz never offers a dish Add to basket would refuse
 *      (`not-in-box`, Chicken Panang on 4 Oct). No answer, a bad answer or a list that would empty the menu
 *      changes nothing: every dish shows, as before. Since OLEO-230 the first screen does not wait for it; the
 *      screens that read the dish list do (settleBox here, holdForDishes in lp-quiz.js).
 *
 * Load order (sections/lp-quiz.liquid): this file (defer), lp-quiz-core.js (defer), lp-quiz-cart.js
 * (defer). lp-quiz.js is preloaded by the section and injected here once the research JSON has arrived
 * and DOMContentLoaded has fired (so the core has run).
 *
 * Test flags (the store is never touched):
 *   ?lpq_unavailable=<handle>[,<handle>]  mark those dishes unavailable in the payload, as Liquid would
 *   ?lpq_noresearch=<handle>[,<handle>]   drop those dishes from the research JSON, as if never researched
 *
 * The pure helpers are exported for node so the sync can prove the merge reproduces dishes.js's legacy
 * keys exactly (the parity check in lp-quiz-sync.mjs). */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (root && root.document) api.boot(root);
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ------------------------------------------------------------------ pure helpers */

  /* The legacy short keys the quiz reads, from a research row. A mirror of the mapping at the foot of the
     prototype's dishes.js (n, i, p, s, b, r, k, a); the sync fails if the two ever disagree. `n` and `i`
     come from the product, and `k` from the builder's Kid Friendly collection (see buildDishes). */
  function researchKeys(r) {
    return {
      p: (r.vegan || r.vegetarian) ? 'plant' : r.protein,
      s: r.spice,
      b: r.pairings && r.pairings.length ? r.pairings : ['standalone'],
      r: r.sauce || 'dry',
      a: r.allergens || []
    };
  }
  /* A dish the research JSON does not carry. It is listed and can be added, but it carries no research
     label: spice NaN is never "near" any level (so it is never picked "For you" on a spice claim and never
     reads as "Not spicy"), protein 'unknown' is not plant (so a plant-only table never sees it), and it has
     no award, collab, gallery or popup copy. */
  var NO_RESEARCH = { p: 'unknown', s: NaN, b: ['standalone'], r: 'dry', a: [] };

  /* One entry per handle, in the builder's order (collection blocks in order, then each collection's own
     order). A dish listed in two blocks is one dish; it is a kids' dish if any block listing it is. */
  function uniqueListed(payloadDishes) {
    var seen = {}, out = [];
    (payloadDishes || []).forEach(function (d) {
      if (!d || !d.handle) return;
      if (seen[d.handle]) { if (d.kids) seen[d.handle].kids = true; return; }
      seen[d.handle] = Object.assign({}, d);
      out.push(seen[d.handle]);
    });
    return out;
  }

  /* buildDishes(listed, research): the DISHES the prototype reads. `listed` is uniqueListed() of the
     payload; unavailable dishes are dropped here. Sorted by handle, the prototype's own order (dishes.js is
     sorted by handle), so every ranking tie breaks the same way as in the prototype. Prices stay in pounds
     like the prototype's `price`. */
  function buildDishes(listed, research, opts) {
    opts = opts || {};
    var R = (research && research.dishes) || {}, drop = opts.noResearch || [];
    var warnings = [], dishes = [], gallery = {}, info = {};
    listed.filter(function (d) { return d.available === true; }).forEach(function (shop) {
      var r = drop.indexOf(shop.handle) < 0 ? R[shop.handle] : undefined;
      var d = {};
      if (r) Object.keys(r).forEach(function (k) { if (k !== 'gallery' && k !== 'info') d[k] = r[k]; });
      Object.assign(d, {
        handle: shop.handle,
        title: shop.title,
        image: shop.image,
        price: shop.price == null ? null : shop.price / 100,
        sub_price: shop.subPrice == null ? null : shop.subPrice / 100,
        compare_price: shop.comparePrice == null ? null : shop.comparePrice / 100,
        variant_id: shop.variantId,
        available: true,
        kids_range: !!shop.kids,
        collection: shop.collection,
        lpq_research: !!r
      });
      Object.assign(d, r ? researchKeys(r) : NO_RESEARCH, { n: shop.title, i: shop.image, k: shop.kids ? 1 : 0 });
      if (!r) warnings.push('no research data for ' + shop.handle + ': listed without research labels');
      else {
        if (r.research_title && r.research_title !== shop.title)
          warnings.push(shop.handle + ': product title "' + shop.title + '" differs from the research title "' + r.research_title + '"; lp-quiz.js copy keyed by the old title will not match');
        if (!!r.kids_range !== !!shop.kids)
          warnings.push(shop.handle + ': research kids_range ' + !!r.kids_range + ', builder collection says ' + !!shop.kids + '; the collection wins');
        if (r.gallery) gallery[shop.title] = r.gallery;
        if (r.info) info[shop.title] = r.info;
      }
      dishes.push(d);
    });
    dishes.sort(function (a, b) { return a.handle < b.handle ? -1 : a.handle > b.handle ? 1 : 0; });
    return { dishes: dishes, gallery: gallery, info: info, warnings: warnings };
  }

  /* packPrices(listed): the one pack price the quiz shows, from the products, in pounds. The quiz prices
     every pack alike (quiz-core.js quote(): packs × PACK_PRICE), as Stocked does today. `sub` is the dish's
     first selling-plan allocation price and `once` its variant price, the two numbers the builder card
     prints (snippets/build-a-bundle-product-card.liquid:166-172). The most common value across the
     available dishes wins; any dish priced differently is reported, because the quiz would misquote it. */
  function packPrices(listed) {
    var avail = listed.filter(function (d) { return d.available === true; });
    var mode = function (key) {
      var n = {}, best = null;
      avail.forEach(function (d) { var v = d[key]; if (v == null) return; n[v] = (n[v] || 0) + 1; if (best === null || n[v] > n[best]) best = v; });
      return best;
    };
    var once = mode('price'), sub = mode('subPrice');
    if (sub === null) sub = once;
    var mismatches = avail.filter(function (d) { return d.price !== once || (d.subPrice != null && d.subPrice !== sub); })
      .map(function (d) { return d.handle + ' ' + d.price + '/' + d.subPrice; });
    return { sub: sub == null ? null : sub / 100, once: once == null ? null : once / 100, mismatches: mismatches };
  }

  /* orderFrom(S, q, dishes, bundle): the object lpQuizAddToBasket takes (docs/landing-pages/quiz-v1/
     lp-quiz-contract.md). Pure, so it can be tested and previewed without a click. */
  function orderFrom(S, q, dishes, bundle, cohort) {
    var byName = {};
    dishes.forEach(function (d) { byName[d.n] = d; });
    var lines = Object.keys(S.box).filter(function (n) { return S.box[n] > 0 && byName[n]; }).map(function (n) {
      var d = byName[n];
      return { variantId: String(d.variant_id), quantity: S.box[n], handle: d.handle, title: d.title };
    });
    var sub = S.purchase !== 'once', weeks = sub && q.frequency ? q.frequency.chosen : null;
    var plans = (bundle && bundle.plansByWeeks) || {};
    /* OLE-17 (27 Sep): the cards are any sold plan now, and plansByWeeks carries 4, 6 and 8 only, so the id is
       also read off bundle.sellingPlans by the name the cart matches ("Every 1 Week", "Every N Weeks"). */
    var named = null, want = weeks == null ? null : 'Every ' + weeks + (weeks === 1 ? ' Week' : ' Weeks');
    ((bundle && bundle.sellingPlans) || []).forEach(function (p) { if (p && p.name === want && p.id != null) named = String(p.id); });
    var planId = plans[weeks] != null ? String(plans[weeks]) : named;
    return {
      lines: lines,
      purchase: sub ? 'sub' : 'once',
      everyWeeks: weeks,
      sellingPlanId: sub && planId != null ? planId : null,
      attributes: cohort
    };
  }

  function list(v) { return (v || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean); }

  /* ------------------------------------------------------------------ Skio's allowed list (OLEO-147) */
  /* The builder's getDynamicBundle request, the same bytes lp-quiz-cart.js skioRequest() sends at the tap (the
     tests compare the two). The box is the build-a-bundle product's, hard-coded there as here. */
  var SKIO_BOX = { url: 'https://api.skio.com/storefront-http/get-dynamic-box', productPlatformId: '15345685561727', timeoutMs: 3000 };
  function skioBoxRequest() {
    return [SKIO_BOX.url, {
      method: 'POST',
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ "productPlatformId": SKIO_BOX.productPlatformId })
    }];
  }
  /* skioSelectable(json): Skio's answer as { '<numeric variant id>': true }, keyed the way lp-quiz-cart.js keys
     it, or null when the answer cannot be trusted as a list: not ok, no DynamicBox, or no variant in it. */
  function skioSelectable(json) {
    var box = json && json.ok && json.DynamicBox;
    if (!box || !Array.isArray(box.selectableProductVariants)) return null;
    var out = {}, n = 0;
    box.selectableProductVariants.forEach(function (p) {
      ((p && p.productVariants) || []).forEach(function (v) {
        var id = String(v && v.platformId != null ? v.platformId : '').split('/').pop();
        if (id && !out[id]) { out[id] = true; n++; }
      });
    });
    return n ? out : null;
  }
  /* notInBox(listed, selectable): the available dishes to hide because Skio's box would refuse them. Pure; the
     caller marks them unavailable, so the dish is out of DISHES, the builder gate, For you, All, the kids' menu
     and any saved Box (QuizCore.normalise drops a name DISHES lacks). `applied` is false, and nothing is hidden,
     when the list was not read (selectable null) or when it would hide every available dish (never an empty
     menu): the quiz then shows every dish, as it did before. */
  function notInBox(listed, selectable) {
    if (!selectable) return { applied: false, reason: 'unread', hidden: [] };
    var avail = (listed || []).filter(function (d) { return d && d.available === true; });
    var hidden = avail.filter(function (d) { return !selectable[String(d.variantId == null ? '' : d.variantId)]; });
    if (avail.length && hidden.length === avail.length) return { applied: false, reason: 'would-empty', hidden: [] };
    return { applied: true, reason: null, hidden: hidden.map(function (d) { return d.handle; }) };
  }

  /* ------------------------------------------------------------------ tracking helpers (OLE-94) */
  /* The quiz's product metrics, Tim's option A (30 Sep): every event and property in the OLE-94 table. Pure,
     so the node tests (docs/landing-pages/quiz-v1/tests/lp-quiz-boot-tracking.test.js) pin each payload. The
     event contract is docs/landing-pages/quiz-v1/posthog-dashboard-spec.md. */

  /* quiz_route: 'home' when the homepage sent the visitor (?entry=home, the same test lp-quiz.js's ENTRY_HOME
     makes), else 'direct' (an ad, or a typed link). */
  function routeOf(search) {
    try { return new URLSearchParams(search || '').get('entry') === 'home' ? 'home' : 'direct'; } catch (e) { return 'direct'; }
  }
  /* How far down the page the bottom of the viewport has been, 0..100. A screen shorter than the viewport is
     seen whole (100). */
  function scrollPct(scrollY, viewportH, pageH) {
    if (!(pageH > 0) || !(viewportH > 0)) return 100;
    return Math.max(0, Math.min(100, Math.round((Math.max(0, scrollY) + viewportH) / pageH * 100)));
  }
  function packsIn(box) {
    var n = 0;
    Object.keys(box || {}).forEach(function (k) { n += box[k] || 0; });
    return n;
  }
  /* quiz_add_to_basket: the Box as the button was pressed. `attrs` is QuizCore.cohortAttributes, the same
     record the order carries, so answers, packs and frequency (chosen and recommended) are all on it. */
  function basketProps(order, q, attrs, attempt) {
    var out = {
      packs: order.lines.reduce(function (a, l) { return a + (l.quantity || 0); }, 0),
      purchase: order.purchase,
      every_weeks: order.everyWeeks,
      price: q && typeof q.payable === 'number' ? Math.round(q.payable * 100) / 100 : null,
      lines: order.lines.length,
      dishes: order.lines.map(function (l) { return l.title; }),
      attempt: attempt
    };
    Object.keys(attrs || {}).forEach(function (k) { out[k] = attrs[k]; });
    return out;
  }
  /* quiz_add_to_basket_result: the basket's own verdict (lp-quiz-cart.js result(): ok, status, code). */
  function basketResultProps(res, ms, attempt) {
    var r = res || {};
    var out = {
      ok: r.ok === true,
      status: r.status || 'FAIL',
      code: r.code || 'unexpected',
      detail: r.ok ? null : String(r.detail || r.message || '').slice(0, 200),
      ms: ms,
      attempt: attempt
    };
    /* OLEO-159: on a PASS, the lines of the quiz's earlier box that this add replaced (0 when there was none) */
    if (typeof r.replacedLines === 'number') out.replaced_lines = r.replacedLines;
    return out;
  }
  /* quiz_dish_changed: one + or − on a dish. `blocked` is a + that added nothing (the Box is full). */
  function dishChangeProps(dish, had, now, packsAfter, dir, from, recommended, step) {
    return {
      dish: dish.n, handle: dish.handle || null,
      change: now - had, blocked: dir > 0 && now === had,
      dish_packs_after: now, packs_after: packsAfter,
      from: from, recommended: !!recommended, step: step, kids_range: !!dish.k
    };
  }

  /* ------------------------------------------------------------------ the attempt (T1, Tim 5 Oct) */
  /* clients/stocked/docs/data-layer/06-tracking-spec.md T1. One quiz attempt is one id, made on the first screen and
     kept in localStorage['lp-quiz.attempt'] beside the quiz state, so a resumed quiz keeps it. PostHog only (Tim, 5 Oct,
     option A: "strictly for us to create those queryable views"): the id never goes on the cart, the order or Klaviyo;
     the quiz cart's section reads it from the PostHog session or from this key. A new attempt starts
     when there is none, when the last one closed (a PASS from Add to order), when it has been quiet for more than
     7 days (the view's rule, src/quiz_attempt_base.sql, so the counts do not jump when the id ships), or when the
     quiz restarts from scratch (?reset=all, a saved state the schema no longer reads, resetDev()). `seq` counts
     this browser's attempts: 1, 2, 3 ... */
  var ATTEMPT_KEY = 'lp-quiz.attempt', ATTEMPT_GAP_MS = 7 * 24 * 60 * 60 * 1000;
  function newAttemptId(cryptoObj) {
    try { if (cryptoObj && typeof cryptoObj.randomUUID === 'function') return cryptoObj.randomUUID(); } catch (e) {}
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }
  function isAttempt(a) { return !!a && typeof a === 'object' && typeof a.id === 'string' && a.id !== '' && typeof a.last_at === 'number'; }
  /* nextAttempt(saved, now, restart, makeId): the saved attempt when it still runs, else a new one. */
  function nextAttempt(saved, now, restart, makeId) {
    if (isAttempt(saved) && !restart && !saved.closed && now - saved.last_at <= ATTEMPT_GAP_MS) return saved;
    var seq = saved && typeof saved === 'object' && saved.seq > 0 ? Math.floor(saved.seq) + 1 : 1;
    return { id: makeId(), started_at: now, last_at: now, closed: false, seq: seq };
  }
  /* The quiz starts from scratch on this load: ?reset=all, or a saved state from a schema the core no longer reads
     (lp-quiz.js readSaved() drops it). `savedV` is the saved state's `v`, peeked before lp-quiz.js runs. */
  function restartOf(search, savedV, schemaVersion) {
    var reset = null;
    try { reset = new URLSearchParams(search || '').get('reset'); } catch (e) {}
    return reset === 'all' || (savedV != null && savedV !== schemaVersion);
  }

  /* ------------------------------------------------------------------ what the Box screen shows (T3) */
  /* shownLists(Core, S, dishes, F, view): the order summary's lists as lp-quiz.js omSectionsC draws them, one array
     of dishes per section, in display order. F is the page's own filter (omFilter(): the Filters ticks, which open
     at the answer), and the arrival ticks when there is none. `foryou` is the For you tab (the adults' picks, then
     the kids' own on a split table), `all` the All tab (Adult Dishes then Kids' Dishes on a table with kids, in the
     live builder's order). Pure, so the node tests pin it against the core. */
  /* OLEO-155: the All tab is "Full Menu" on the page (key still 'all') and has no filter at all (Tim, 5 Oct), so it
     always lists the whole range whatever F is; F narrows For you only. */
  /* OLEO-154: OLEO-155's Full Menu without filters and For you of sixteen are V2A and V2B only (the template's variant;
     Core.VARIANT on the page). Today's quiz filters All as live does and lists eight. */
  function shownLists(Core, S, dishes, F, view, variant) {
    var v2 = Core.v11 ? Core.v11(variant === undefined ? Core.VARIANT : variant) : '';
    F = v2 && view === 'all' ? Core.menuFilter({}) : F || Core.menuFilter({ diet: Core.dietTicks(S), heat: Core.heatTicks(S) });
    var L = Core.menuLists(S, dishes), split = L.kids.length > 0;
    if (view !== 'all') {
      var fy = [Core.forYouList(S, dishes, 'table', F.adults, F.levels, v2)];
      if (split) fy.push(Core.forYouList(S, dishes, 'kids', F.kids, null, v2));
      return fy;
    }
    var order = Core.BUILDER_LISTED;
    var ordered = function (list) {
      var at = function (n) { var i = order.indexOf(n); return i < 0 ? order.length : i; }, rk = {};
      list.forEach(function (d, i) { rk[d.n] = i; });
      return list.slice().sort(function (a, b) { return at(a.n) - at(b.n) || rk[a.n] - rk[b.n]; });
    };
    var heldKids = L.kidsRange.some(function (d) { return (S.box[d.n] || 0) > 0; });
    if (split || Core.household(S).kids > 0 || heldKids)
      return [ordered(L.table.filter(F.adults)), ordered((split ? L.kids : L.kidsRange).filter(F.kids))];
    return [ordered(L.table.filter(F.adults))];
  }
  var handlesOf = function (list) { return list.map(function (d) { return d.handle || d.n; }); };
  /* quiz_step's extra properties on the Box: what For you recommended, in order, and the top of All. */
  /* F is For you's filter (OLEO-155: Full Menu has none, so shownLists ignores F for it). */
  function boxShownProps(Core, S, dishes, F, variant) {
    var fy = shownLists(Core, S, dishes, F, 'foryou', variant), all = shownLists(Core, S, dishes, F, 'all', variant);
    var out = { for_you: handlesOf(fy[0]) };
    if (fy.length > 1) out.for_you_kids = handlesOf(fy[1]);
    out.menu_order = handlesOf([].concat.apply([], all)).slice(0, 12);
    return out;
  }
  /* quiz_dish_changed.position: 1-based, within the section list the dish sits in on the view showing (a popup's
     dish takes its row's place behind it); null when no list shows it. */
  function positionIn(lists, dish) {
    for (var i = 0; i < lists.length; i++) {
      for (var j = 0; j < lists[i].length; j++) if (lists[i][j].n === dish.n) return j + 1;
    }
    return null;
  }
  /* T4: answer detail the record drops, for the events only (never the cart, which Stocked reads). */
  function answerDetail(Core, S) {
    var out = {};
    var order = Array.isArray(S.reasonOrder) ? S.reasonOrder : [];
    out.quiz_reason_order = order.join(',');
    var at = S.problem ? order.indexOf(S.problem) : -1;
    out.quiz_reason_position = at < 0 ? null : at + 1;
    out.quiz_spice_levels = (S.heatAdult || []).join(',');
    var H = Core.household(S);
    if (H.answered && H.kids > 0) out.quiz_spice_kids_levels = Core.kidsHeat(S).join(',');
    return out;
  }

  /* ------------------------------------------------------------------ Edit from the quiz cart (OLEO-186) */
  /* The quiz cart's Edit on a box the quiz added (sections/lp-quiz-cart-return.liquid) opens this page with
     ?edit=<_bundle_id>#box and leaves the box as the cart holds it in localStorage 'lp-quiz.edit': { bundleId, at,
     lines: [{ variantId, quantity }], purchase, weeks, attributes: the cart's quiz_* }. Before lp-quiz.js reads its
     saved state, that state's Box, purchase and Frequency are set to the cart's, so Build your menu opens on the same
     dishes, Packs and plan, and Add to order replaces the box at that plan (lp-quiz-cart.js, OLEO-159). The answers are
     the saved state's; when it has none this schema reads (a push changed the shape since), they are rebuilt from the
     quiz_* attributes. Without ?edit= nothing here runs. */
  var EDIT_KEY = 'lp-quiz.edit', EDIT_MAX_AGE_MS = 30 * 60 * 1000;
  /* answersFrom(Core, attrs): the answers the order record carries, as a raw state for QuizCore.normalise. The spice
     sliders are written as their top level, so each comes back as every level up to it. null without a household. */
  function answersFrom(Core, a) {
    a = a || {};
    var num = function (x) { return /^[0-9]+$/.test(x || '') ? +x : null; };
    var upTo = function (x) { var n = num(x), out = []; for (var i = 0; n !== null && i <= Math.min(n, 3); i++) out.push(i); return out.length ? out : null; };
    var adults = num(a.quiz_adults);
    if (!adults) return null;
    var raw = { adults: adults, kids: num(a.quiz_kids) || 0 };
    if (a.quiz_kids_same === 'yes') raw.kidsSplit = 'same'; else if (a.quiz_kids_same === 'no') raw.kidsSplit = 'own';
    if (a.quiz_diet && a.quiz_diet !== 'unanswered') raw.diet = a.quiz_diet;
    if (a.quiz_reason && a.quiz_reason !== 'unanswered') raw.problem = a.quiz_reason;
    (Core.INTENTIONS || []).forEach(function (i) { if (String(i.phrase).toLowerCase() === a.quiz_intention_label) raw.intention = i.id; });
    if (upTo(a.quiz_spice)) raw.heatAdult = upTo(a.quiz_spice);
    if (upTo(a.quiz_spice_kids)) raw.heatKids = upTo(a.quiz_spice_kids);
    return raw;
  }
  /* editRestore(Core, saved, payload, bundleId, dishes, now): the saved state ({ v, idx, S }) to write, with
     `source` saying where the answers came from ('saved' or 'attributes'), or null to leave the saved state as it is
     (no box for this id, a payload older than 30 minutes, or no answers to rebuild). Pure. */
  function editRestore(Core, saved, payload, bundleId, dishes, now) {
    var p = payload && typeof payload === 'object' && payload.bundleId === bundleId && typeof payload.at === 'number'
      && now - payload.at >= 0 && now - payload.at <= EDIT_MAX_AGE_MS && Array.isArray(payload.lines) && payload.lines.length ? payload : null;
    if (!bundleId || !p) return null;
    var good = saved && typeof saved === 'object' && saved.v === Core.SCHEMA_VERSION && saved.S && typeof saved.S === 'object' && saved.S.adults != null;
    var S = {}, source = 'saved';
    if (good) Object.keys(saved.S).forEach(function (k) { S[k] = saved.S[k]; });
    else { var raw = answersFrom(Core, p.attributes); if (!raw) return null; S = raw; source = 'attributes'; }
    var byVariant = {}, box = {};
    (dishes || []).forEach(function (d) { if (d && d.variant_id != null) byVariant[String(d.variant_id)] = d.n; });
    p.lines.forEach(function (l) {
      var n = l && byVariant[String(l.variantId)], q = l && l.quantity;
      if (n && typeof q === 'number' && q > 0) box[n] = (box[n] || 0) + q;
    });
    if (!Object.keys(box).length) return null;
    S.box = box;
    S.boxTouched = true;
    S.sheetOpen = false;
    if (p.purchase === 'once') S.purchase = 'once';
    else { S.purchase = 'sub'; if (typeof p.weeks === 'number' && p.weeks > 0) S.frequency = p.weeks; }
    return { v: Core.SCHEMA_VERSION, idx: good && typeof saved.idx === 'number' ? saved.idx : 0, S: S, source: source };
  }
  /* applyEdit(win, dishes): the page side. Runs once, before lp-quiz.js; drops edit from the URL (the hash stays) and
     the payload from storage, so a reload keeps what the customer changes after it. */
  function applyEdit(win, dishes) {
    var id = null;
    try { id = new URLSearchParams(win.location.search).get('edit'); } catch (e) {}
    if (!id) return null;
    var payload = null, saved = null, out = null;
    try { payload = JSON.parse(win.localStorage.getItem(EDIT_KEY) || 'null'); } catch (e) {}
    try { saved = JSON.parse(win.localStorage.getItem('lp-quiz') || 'null'); } catch (e) {}
    try { out = editRestore(win.QuizCore, saved, payload, id, dishes, Date.now()); } catch (e) { out = null; }
    if (out) try { win.localStorage.setItem('lp-quiz', JSON.stringify({ v: out.v, idx: out.idx, S: out.S })); } catch (e) { out = null; }
    try { win.localStorage.removeItem(EDIT_KEY); } catch (e) {}
    try { var u = new URL(win.location.href); u.searchParams.delete('edit'); win.history.replaceState(win.history.state, '', u.pathname + u.search + u.hash); } catch (e) {}
    return { bundleId: id, restored: out ? out.source : null };
  }

  /* ------------------------------------------------------------------ the dish popup, off the boot path (OLEO-230) */
  /* lp-quiz-dish-popup.js is needed only once a dish's photo or name is tapped on Build your menu, so boot() loads it
     after the first screen has drawn, at the first idle moment (Safari has no requestIdleCallback: a timer instead),
     and at once when a dish is tapped before then. POPUP_TRIGGERS is the popup's own TRIGGERS, the photos and names it
     marks with data-dp-open (a node test holds the two equal); before it has run, a tap on one is queued and opened
     when it has. */
  var POPUP_TRIGGERS = '.om-rowb-ph, .om-rowb-dn, .om-row-ph, .om-row .om-dn, .om-card .om-ph img, .om-card .om-dn';
  var POPUP_IDLE = { timeoutMs: 3000, fallbackMs: 1500 };

  /* ------------------------------------------------------------------ the page */
  function boot(win) {
    var doc = win.document, el = doc.getElementById('lp-quiz-data');
    if (!el) { console.error('lp-quiz: #lp-quiz-data is missing; the quiz cannot start.'); return; }
    var data;
    try { data = JSON.parse(el.textContent); }
    catch (e) { console.error('lp-quiz: #lp-quiz-data is not valid JSON.', e); return; }

    var qs = new URLSearchParams(win.location.search);
    var mockUnavailable = list(qs.get('lpq_unavailable')), noResearch = list(qs.get('lpq_noresearch'));
    var listed = uniqueListed(data.dishes);
    listed.forEach(function (d) { if (mockUnavailable.indexOf(d.handle) >= 0) d.available = false; });
    var prices = packPrices(listed);

    var lpQuiz = win.lpQuiz = {
      data: data, bundle: data.bundle, listed: listed, prices: prices,
      variant: data.variant,                             // OLEO-154: the template (section v11_variant); lp-quiz-core.js reads it as it loads
      test: { unavailable: mockUnavailable, noResearch: noResearch },
      warnings: [], ready: false
    };
    if (prices.mismatches.length) lpQuiz.warnings.push('pack prices differ: ' + prices.mismatches.join(', ') + '; the quiz shows ' + prices.sub + ' / ' + prices.once + ' for every pack');
    win.LPQ_BASE = data.assetBase;                     // the sync writes local media as LPQ_BASE + 'lp-quiz-…'
    win.lpQuizSkip = function () { win.location.href = data.builderUrl; };

    /* The prototype's D (state drawer) and R (start over, no confirmation) keys are for the review board. On
       a customer's keyboard R would wipe the quiz, so neither reaches the prototype's handler here. */
    win.addEventListener('keydown', function (e) {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target && e.target.tagName)) return;
      if (e.key === 'd' || e.key === 'D' || e.key === 'r' || e.key === 'R') e.stopImmediatePropagation();
    }, true);

    if (prices.once == null) { fail('no available dish has a price'); return; }

    var domReady = new Promise(function (res) {
      if (doc.readyState !== 'loading') res(); else doc.addEventListener('DOMContentLoaded', function () { res(); }, { once: true });
    });
    /* OLEO-230: Skio's box list (OLEO-147) is asked for here, at the same moment as before, but the first screen no
       longer waits for it: lp-quiz.js is injected once the research JSON and DOMContentLoaded are in. When Skio has
       answered by then, the dishes its box would refuse leave before DISHES is built, exactly as before. When it answers
       later, settleBox() takes them out of DISHES, the builder gate and the quiz state, and every screen that reads the
       dish list waits for that (lp-quiz.js holdForDishes, on lpQuiz.dishesFinal and lpQuiz.dishesReady). A return from
       the quiz cart (?edit=) opens on Build your menu with the cart's dishes, so it still waits for Skio first. A failure
       or a timeout is the same as before: every dish shows. */
    var skio = skioBox(), skioIn = false, skioSel = null, editing = !!qs.get('edit');
    skio.then(function (sel) { skioIn = true; skioSel = sel; });
    lpQuiz.dishesFinal = false;
    Promise.all([research(data.research), domReady, editing ? skio : null]).then(function (got) {
      var R = got[0];
      if (!win.QuizCore) throw new Error('lp-quiz-core.js did not load');
      /* OLEO-147: a dish Skio's box would refuse is hidden before anything reads the list */
      if (skioIn) hideNotInBox(skioSel);
      var built = buildDishes(listed, R, { noResearch: noResearch });
      lpQuiz.warnings = lpQuiz.warnings.concat(built.warnings);
      if (!built.dishes.length) throw new Error('no available dishes in the payload');
      /* the globals the prototype's dishes.js and dish-info.js declare */
      win.DISHES = built.dishes;
      win.GALLERY = built.gallery;
      win.IMG = (R && R.img_base) || '';
      win.DISH_INFO = built.info;
      win.DISH_INFO_SHARED = (R && R.shared) || {};
      lpQuiz.dishes = built.dishes;
      /* The builder gate (quiz-core.js pool(): "in stock AND on the live builder"). The prototype captures the
         builder's list by hand; here it IS the builder's collections, so the list is replaced in place (the
         core and lp-quiz.js's OM_BUILDER_ORDER hold the same array) with the listed titles in builder order. */
      var gate = win.QuizCore.BUILDER_LISTED;
      gate.length = 0;
      listed.forEach(function (d) { if (d.available) gate.push(d.title); });
      if (lpQuiz.warnings.length) console.warn('lp-quiz: ' + lpQuiz.warnings.join('\n'));
      if (skioIn) { lpQuiz.dishesFinal = true; lpQuiz.dishesReady = Promise.resolve(); }
      else lpQuiz.dishesReady = skio.then(settleBox);
      /* OLEO-186: Edit from the quiz cart sets the saved Box and Frequency to the cart's before lp-quiz.js reads them */
      lpQuiz.edit = applyEdit(win, built.dishes);
      /* T1: whether this load starts the quiz from scratch, read before lp-quiz.js replaces the saved state */
      var savedV = null;
      try { var sv = JSON.parse(win.localStorage.getItem('lp-quiz') || 'null'); savedV = sv && sv.v != null ? sv.v : null; } catch (e) {}
      lpQuiz.restart = restartOf(win.location.search, savedV, win.QuizCore.SCHEMA_VERSION);
      /* OLEO-230: the popup no longer loads with lp-quiz.js (see POPUP_TRIGGERS). ?popup=<dish> (QZ-15, the Paper
         map's capture) still loads it beside lp-quiz.js, because lp-quiz.js opens it at the window's load event. */
      var main = load(data.scripts.main), early = qs.get('popup') != null ? loadPopup() : null;
      return main.then(function () {
        wire(win, lpQuiz);
        doc.addEventListener('click', tapBeforePopup);
        if (early) return early;
        whenIdle(loadPopup);
      });
    }).then(function () {
      return lpQuiz.dishesReady;                       // OLEO-230: ready also means the dish list is final
    }).then(function () {
      lpQuiz.ready = true;
      doc.documentElement.setAttribute('data-lp-quiz-ready', '');
    }).catch(function (e) { fail(e && e.message ? e.message : String(e), e); });

    function load(src) {
      return new Promise(function (res, rej) {
        var s = doc.createElement('script');
        s.src = src; s.async = false;                  // executes in insertion order: lp-quiz.js, then the popup
        s.onload = function () { res(); };
        s.onerror = function () { rej(new Error('could not load ' + src)); };
        doc.body.appendChild(s);
      });
    }
    /* OLEO-230: the dish popup, loaded once. Its failure never fails the quiz (the menu works without it); the next
       tap on a dish tries again. A dish tapped before it ran opens when it has, if that row is still on the page. */
    var popupLoading = null, popupQueued = null;
    function loadPopup() {
      if (!popupLoading) popupLoading = load(data.scripts.popup).then(function () {
        doc.documentElement.setAttribute('data-lp-quiz-popup-ready', '');
        var x = popupQueued; popupQueued = null;
        if (x != null && win.dishPopup && doc.querySelector('.om-screen [data-dish="' + x + '"]')) win.dishPopup.open(x);
      }, function (e) {
        popupLoading = null;
        console.warn('lp-quiz: ' + (e && e.message ? e.message : String(e)) + '; the dish popup loads on the next tap');
      });
      return popupLoading;
    }
    lpQuiz.loadPopup = loadPopup;                      // for the proof runners
    function tapBeforePopup(e) {
      if (win.dishPopup) return;                       // it has run: its own listener opens the dish
      var t = e.target && e.target.closest ? e.target.closest(POPUP_TRIGGERS) : null;
      var row = t && t.closest('.om-screen [data-dish]');
      if (!row || row === t || e.target.closest('.om-ctl')) return;
      popupQueued = +row.getAttribute('data-dish');
      loadPopup();
    }
    function whenIdle(fn) {
      if (typeof win.requestIdleCallback === 'function') win.requestIdleCallback(function () { fn(); }, { timeout: POPUP_IDLE.timeoutMs });
      else win.setTimeout(fn, POPUP_IDLE.fallbackMs);
    }
    /* OLEO-147: Skio's allowed list, read once, in parallel with the research JSON. It never rejects: a failure, a
       timeout (SKIO_BOX.timeoutMs) or an unusable answer resolves null, and the quiz shows every dish as before. */
    function skioBox() {
      var req = skioBoxRequest(), ctl = typeof win.AbortController === 'function' ? new win.AbortController() : null, timer = 0;
      var init = ctl ? Object.assign({}, req[1], { signal: ctl.signal }) : req[1];
      var timeout = new Promise(function (_, rej) {
        timer = win.setTimeout(function () { if (ctl) ctl.abort(); rej(new Error('timeout after ' + SKIO_BOX.timeoutMs + 'ms')); }, SKIO_BOX.timeoutMs);
      });
      return Promise.race([Promise.resolve().then(function () { return win.fetch(req[0], init); }), timeout]).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      }).then(function (json) {
        win.clearTimeout(timer);
        var sel = skioSelectable(json);
        if (!sel) lpQuiz.skioError = 'no usable selectableProductVariants';
        return sel;
      }, function (e) {
        win.clearTimeout(timer);
        lpQuiz.skioError = e && e.message ? e.message : String(e);
        return null;
      });
    }
    /* OLEO-147: mark the listed dishes Skio's box would refuse as unavailable (so buildDishes and the gate drop them),
       and name the outcome in the warnings. Returns notInBox()'s verdict. */
    function hideNotInBox(sel) {
      var box = lpQuiz.notInBox = notInBox(listed, sel);
      listed.forEach(function (d) { if (box.hidden.indexOf(d.handle) >= 0) { d.available = false; d.notInBox = true; } });
      if (box.hidden.length) lpQuiz.warnings.push('not in Skio\'s box, hidden: ' + box.hidden.join(', '));
      else if (!box.applied) lpQuiz.warnings.push('Skio\'s box list ' + (box.reason === 'unread' ? 'was not read (' + (lpQuiz.skioError || 'no list') + ')' : 'would hide every dish') + '; every available dish is shown');
      return box;
    }
    /* OLEO-230: Skio answered after DISHES was built. The same dishes leave as when it answers first: out of DISHES (in
       place, so every holder of the array sees it, renumbered as lp-quiz.js numbers it when it has), out of the builder
       gate, and out of the quiz state's Box and picks when lp-quiz.js has read it (before that, its normalise() reads the
       saved state against the final DISHES). Then the list is final, and the dish screens draw. */
    function settleBox(sel) {
      try {
        var box = hideNotInBox(sel), gone = {}, D = win.DISHES || [], gate = win.QuizCore.BUILDER_LISTED, i;
        listed.forEach(function (d) { if (box.hidden.indexOf(d.handle) >= 0) gone[d.title] = true; });
        if (box.hidden.length) {
          for (i = D.length - 1; i >= 0; i--) if (box.hidden.indexOf(D[i].handle) >= 0) D.splice(i, 1);
          if (D.length && 'x' in D[0]) D.forEach(function (d, k) { d.x = k; });
          for (i = gate.length - 1; i >= 0; i--) if (gone[gate[i]]) gate.splice(i, 1);
          dropFromState(gone);
        }
        if (box.hidden.length || !box.applied) console.warn('lp-quiz: ' + lpQuiz.warnings[lpQuiz.warnings.length - 1]);
      } catch (e) { console.error('lp-quiz: Skio\'s box list could not be applied', e); }
      lpQuiz.dishesFinal = true;
    }
    /* eslint-disable no-undef */
    function dropFromState(gone) {
      var St = null;
      try { St = typeof S === 'object' && S ? S : null; } catch (e) {}   // lp-quiz.js's S, once it has run
      if (!St) return;
      if (St.box && typeof St.box === 'object') Object.keys(St.box).forEach(function (n) { if (gone[n]) delete St.box[n]; });
      ['duels', 'kidDuels'].forEach(function (k) { if (Array.isArray(St[k])) St[k] = St[k].map(function (n) { return gone[n] ? null : n; }); });
      St.boxTouched = !!St.boxTouched && Object.keys(St.box || {}).length > 0;
    }
    /* eslint-enable no-undef */
    /* One retry, then the quiz starts without research labels rather than not at all. */
    function research(url) {
      var get = function () { return win.fetch(url, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); }); };
      return get().catch(get).catch(function (e) {
        lpQuiz.warnings.push('research JSON failed (' + e.message + '); every dish is listed without research labels');
        return { dishes: {}, shared: {} };
      });
    }
    /* A failed boot reloads the page ONCE before it shows the fallback (qz-port, 2026-09-25, after Tim's load at
       09:44:50 UTC hit the fallback 26 s after a sync uploaded lp-quiz.js, .css and -core.js; the reload at
       09:45:17 booted). A theme upload replaces the lp-quiz files one by one, and a page rendered mid-upload
       can reference a script version the CDN does not serve yet, or a core and an app from two builds. A fresh
       page fetches fresh asset URLs, so one reload clears it. sessionStorage holds the retry for 60 s, so a
       real fault shows the fallback after one reload instead of looping; where storage is blocked there is no
       retry. Every failure is also sent to PostHog as quiz_boot_failed, with the reason and the asset
       versions, because a failure before PostHog starts never reaches its exception capture. */
    function fail(why, err) {
      console.error('lp-quiz: ' + why, err || '');
      var ver = {}, ph = win.posthog;
      [].forEach.call(doc.querySelectorAll('script[src*="/lp-quiz"], link[href*="/lp-quiz"]'), function (s) {
        var u = s.src || s.href; ver[u.split('/').pop().split('?')[0]] = (u.split('?v=')[1] || '').slice(-10);
      });
      var retried = false;
      try {
        var at = +win.sessionStorage.getItem('lp-quiz.bootRetry') || 0;
        retried = Date.now() - at < 60000;
        if (!retried) win.sessionStorage.setItem('lp-quiz.bootRetry', String(Date.now()));
      } catch (e) { retried = true; }
      try { if (ph && typeof ph.capture === 'function') ph.capture('quiz_boot_failed', { reason: why, retried: retried, versions: ver }, { send_instantly: true }); } catch (e) {}
      if (!retried) { win.setTimeout(function () { win.location.reload(); }, 300); return; }
      var app = doc.getElementById('app');
      if (app && !app.children.length) app.innerHTML = '<div class="screen"><div class="pad" style="padding-top:40px">'
        + '<h2>Sorry, the quiz didn’t load.</h2><p style="margin:12px 0 24px">Please refresh the page, or build your box yourself.</p>'
        + '<a class="btn ink" href="' + data.builderUrl + '">Build your box</a></div></div>';
    }
  }

  /* ------------------------------------------------------------------ after lp-quiz.js has run */
  /* The prototype declares these as top-level functions, so they are properties of window and every inline
     onclick="…" and internal call resolves the replacement at call time. S, quote and render are the
     prototype's own globals, read here by name once lp-quiz.js has run. */
  function wire(win, lpQuiz) {
    var doc = win.document, pending = null;
    var toast = doc.getElementById('lp-quiz-toast'), toastTimer = 0;
    function say(text) {
      if (!toast) return;
      toast.textContent = text; toast.hidden = false;
      clearTimeout(toastTimer); toastTimer = setTimeout(function () { toast.hidden = true; }, 8000);
    }
    /* eslint-disable no-undef */
    /* PostHog, if the page has it (lp-posthog in the quiz's header group). Every capture goes through track(),
       which never throws into the quiz. BEACON is for an event the page may unload under: the basket
       navigates to /cart straight after a PASS, and pagehide is the end of every visit. */
    var BEACON = { transport: 'sendBeacon', send_instantly: true };
    function ph() { var p = win.posthog; return p && typeof p.capture === 'function' ? p : null; }
    /* quiz_route (OLE-94 metric 3) is written onto every quiz event here, and registered for the session so
       autocapture and the quiz cart's events carry it too (the cart shares the PostHog cookie, so the same
       session). register_for_session, not register, so it never sticks to this person's next visit through a
       different route. It is re-registered with every event: on the dev theme (30 Sep) a single call at boot
       reached the first quiz_step only, then the session store no longer had it. */
    var route = routeOf(win.location.search);
    /* OLEO-154: the template. OLEO-179 (Tim, 7 Oct: "surely the V2 doesn't need a quiz variant because it's already
       obvious from the quiz line"): on Quiz V2 every quiz event carries `quiz` = "v2" (QuizCore.quizVersion; the dropped
       V2B "v2b"), the three without the record (quiz_screen_left, quiz_dish_changed, quiz_add_to_basket_result)
       included, so PostHog splits versions on `quiz` alone. There is no quiz_variant. Today's quiz adds nothing here:
       those three still send no `quiz`, and the record's events keep "v1", exactly as live. Left in
       sessionStorage 'lp-quiz.variant' for the quiz cart's return link (sections/lp-quiz-cart-return.liquid); today's
       quiz clears it, so that link keeps its live href. */
    var variant = win.QuizCore && typeof win.QuizCore.v11 === 'function' ? win.QuizCore.v11(lpQuiz && lpQuiz.variant) : '';
    /* OLEO-220: the version the record and the events carry. Quiz V3 runs V2A's paths (variant "a") but is "v3" on
       `quiz` (events and the cart attribute); everywhere else version is variant, so nothing else changes. */
    var version = variant && win.QuizCore.isV3 && win.QuizCore.isV3(lpQuiz && lpQuiz.variant) ? win.QuizCore.V3_VARIANT : variant;
    /* OLEO-220 (Tim, 2A): the cart's return link reads the template from here, so Quiz V3 leaves "v3" (its view is
       lp-quiz-v3); V2A and V2B leave "a" / "b" as before */
    try { if (variant) win.sessionStorage.setItem('lp-quiz.variant', version); else win.sessionStorage.removeItem('lp-quiz.variant'); } catch (e) {}
    /* The session store did not carry it to /cart either (30 Sep), so the quiz cart's section reads it from here. */
    try { win.sessionStorage.setItem('lp-quiz.route', route); } catch (e) {}
    /* T1: the attempt. Every storage access is wrapped; where storage is blocked the id lives in memory for the page. */
    var quizAttempt = null;                                // not `attempt`: goCheckout's attempt is the basket press count
    function saveAttempt() { try { win.localStorage.setItem(ATTEMPT_KEY, JSON.stringify(quizAttempt)); } catch (e) {} }
    function readAttempt() { try { return JSON.parse(win.localStorage.getItem(ATTEMPT_KEY) || 'null'); } catch (e) { return null; } }
    function startAttempt(restart) {
      var saved = readAttempt();
      if (!isAttempt(saved) && quizAttempt) saved = quizAttempt;   // storage blocked: the page's own attempt is the last one
      quizAttempt = nextAttempt(saved, Date.now(), restart, function () { return newAttemptId(win.crypto); });
      saveAttempt();
      return quizAttempt;
    }
    startAttempt(lpQuiz.restart === true);
    lpQuiz.attempt = function () { return quizAttempt; };
    /* every track(): a tab left open past the 7 days starts a new attempt; otherwise last_at moves on */
    function touchAttempt() {
      var now = Date.now();
      if (!quizAttempt || now - quizAttempt.last_at > ATTEMPT_GAP_MS) startAttempt(false);
      else { quizAttempt.last_at = now; saveAttempt(); }
      return quizAttempt;
    }
    function closeAttempt() { if (quizAttempt) { quizAttempt.closed = true; saveAttempt(); } }
    function track(name, props, opts) {
      try {
        var a = touchAttempt();
        var p = ph(); if (!p) return;
        if (typeof p.register_for_session === 'function') p.register_for_session({ quiz_route: route, quiz_attempt_id: a.id });
        props = props || {}; props.quiz_route = route;
        if (variant) props.quiz = win.QuizCore.quizVersion(version);   // OLEO-179: Quiz V2 only; live sends as before (OLEO-220: "v3" on V3)
        props.quiz_attempt_id = a.id; props.quiz_attempt_seq = a.seq;
        p.capture(name, props, opts);
      } catch (e) {}
    }
    /* The order's own record (QuizCore.cohortAttributes), or {} when the state cannot be quoted yet. T4 adds the
       answer detail the record drops (reason order and position, every spice level ticked), on the events only. */
    function record(q) {
      var rec;
      try { q = q || quote(); rec = win.QuizCore.cohortAttributes(S, q.frequency, version); } catch (e) { return {}; }   // OLEO-178: quiz "v2" on Quiz V2 ("v3" on V3)
      try { var more = answerDetail(win.QuizCore, S); Object.keys(more).forEach(function (k) { rec[k] = more[k]; }); } catch (e) {}
      return rec;
    }
    /* T3: the page's own Filters ticks (lp-quiz.js omFilter), or the arrival ticks where it cannot be read */
    function pageFilter(v) { try { return typeof omFilter === 'function' ? omFilter(v) : null; } catch (e) { return null; } }   // v: 'foryou' | 'all', or the tab showing
    function viewNow() { try { return omView === 'all' ? 'all' : 'foryou'; } catch (e) { return 'foryou'; } }
    var attempts = 0;
    lpQuiz.orderPreview = function () { var q = quote(); return orderFrom(S, q, win.DISHES, lpQuiz.bundle, win.QuizCore.cohortAttributes(S, q.frequency, version)); };
    /* OLEO-220: lp-quiz-email.js sends its three events through track(), so they carry quiz_route, quiz and the attempt */
    if (version === 'v3') lpQuiz.track = track;
    /* The order button while an add is in flight: disabled and aria-busy, as before. OLEO-197 (Quiz V2 only): it also
       reads BUSY_LABEL and keeps its colour (.lpq-busy in lp-quiz.css), so a tap that seems to do nothing is not
       repeated; the label it had comes back when the add ends. Off, a button the bar drew as over the ceiling stays off. */
    var BUSY_LABEL = 'Adding your box…';
    function busy(on) {
      var b = doc.querySelector('.fq-bar .om-go, .bs-atc'); if (!b) return;
      b.disabled = on ? true : b.getAttribute('aria-disabled') === 'true';
      if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
      if (!variant || !b.classList) return;
      var label = b.querySelector('span');
      if (on) b.classList.add('lpq-busy'); else b.classList.remove('lpq-busy');
      if (!label) return;
      if (on) { if (label.getAttribute('data-lpq-label') == null) label.setAttribute('data-lpq-label', label.textContent); label.textContent = BUSY_LABEL; }
      else if (label.getAttribute('data-lpq-label') != null) { label.textContent = label.getAttribute('data-lpq-label'); label.removeAttribute('data-lpq-label'); }
    }
    lpQuiz.busy = function () { return !!pending; };
    win.goCheckout = function () {
      if (pending) return pending;
      var q = quote(), Core = win.QuizCore;
      if (!q.packs || q.packs > Core.liveCeiling() || !q.frequency) return;   // the prototype's own guard
      var order = orderFrom(S, q, win.DISHES, lpQuiz.bundle, Core.cohortAttributes(S, q.frequency, version));   // OLEO-178: the record's quiz is the version
      if (variant) order.variant = variant;            // OLEO-154: lp-quiz-cart.js replaces the earlier quiz box on V2A / V2B only
      if (toast) toast.hidden = true;
      busy(true);
      /* metrics 5, 7 and 8: the press, with the Box, the price and the whole record at that moment */
      var attempt = ++attempts, t0 = Date.now();
      track('quiz_add_to_basket', basketProps(order, q, order.attributes, attempt), BEACON);
      /* metrics 6 and 9: the basket's verdict. A PASS has already set location to /cart, so it goes by beacon.
         T1: a PASS closes the attempt; the next visit to the quiz starts a new one. */
      var verdict = function (res) {
        track('quiz_add_to_basket_result', basketResultProps(res, Date.now() - t0, attempt), BEACON);
        if (res && res.ok === true) closeAttempt();
      };
      var add = win.lpQuizAddToBasket;
      pending = Promise.resolve().then(function () {
        if (typeof add !== 'function') throw new Error('window.lpQuizAddToBasket is not loaded');
        return add(order);
      }).then(function (res) {
        lpQuiz.lastResult = res;
        verdict(res);
        if (res && res.ok) { basketOk = true; return res; }   // the cart navigates to /cart; the button stays busy
        console.warn('lp-quiz: add to basket did not complete', res);
        say(res && res.message ? res.message : 'We couldn’t add your box just now. Please try again.');
        busy(false); pending = null;
        return res;
      }, function (e) {
        console.error('lp-quiz: add to basket failed', e);
        verdict({ ok: false, status: 'FAIL', code: 'exception', detail: e && e.message ? e.message : String(e) });
        say('We couldn’t add your box just now. Please try again.');
        busy(false); pending = null;
      });
      return pending;
    };
    /* quiz_dish_changed (metric 10): every + and − on a dish, from a row (For you or All) or the dish popup,
       which both go through the prototype's qty(). A + the full Box refuses is sent too, as blocked. */
    var basketOk = false;
    function dishFrom() {
      /* open = not [hidden]. Not .on: dish-popup.js adds .on in a requestAnimationFrame, which a background tab
         never runs (the dev-theme proof, 30 Sep, sent a popup add as for_you from a hidden browser). */
      if (doc.querySelector('#dp:not([hidden]):not(.closing)')) return 'popup';
      try { return omView === 'all' ? 'menu' : 'for_you'; } catch (e) { return null; }
    }
    function recommended(d) {
      try {
        var Core = win.QuizCore, names = Core.forYouList(S, win.DISHES, 'table').concat(Core.forYouList(S, win.DISHES, 'kids')).map(function (x) { return x.n; });
        return names.indexOf(d.n) >= 0;
      } catch (e) { return false; }
    }
    if (typeof win.qty === 'function') {
      var tap = win.qty;
      win.qty = function (x, n) {
        var d = win.DISHES && win.DISHES[x], had = d ? (S.box[d.n] || 0) : 0;
        /* T3: where the dish sat, read before the tap (a tap can change which lists All shows) */
        var pos = null;
        try { if (d) pos = positionIn(shownLists(win.QuizCore, S, win.DISHES, pageFilter(), viewNow(), variant), d); } catch (e) {}
        var out = tap.apply(this, arguments);
        try {
          if (d) {
            var now = S.box[d.n] || 0;
            if (now !== had || n > 0) {
              var changed = dishChangeProps(d, had, now, packsIn(S.box), n, dishFrom(), recommended(d), lastStep);
              changed.position = pos;
              track('quiz_dish_changed', changed);
            }
          }
        } catch (e) {}
        return out;
      };
    }
    win.goBuilder = function () { win.location.href = lpQuiz.data.builderUrl; };
    /* A dish without research data shows no research label on its row: no award, collab, spice or diet. */
    var unresearched = function (d) { return d && d.lpq_research === false; };
    if (typeof win.omAttrs === 'function') { var attrs = win.omAttrs; win.omAttrs = function (d) { return unresearched(d) ? [] : attrs.apply(this, arguments); }; }
    if (typeof win.omHeatNote === 'function') { var heat = win.omHeatNote; win.omHeatNote = function (d) { return unresearched(d) ? '' : heat.apply(this, arguments); }; }
    /* quiz_step (qz-port, 2026-09-25): one PostHog event each time the quiz shows a different screen, first
       screen included, forward, Back and the browser's back alike (Q25 B pushes one history entry a screen).
       A custom event, NOT a $pageview: lp-posthog captures exactly one $pageview per page load
       (capture_pageview: true, never 'history_change'), which is what every other LP reports, so a pageview
       per screen would inflate the quiz's pageviews and bounce rate against Families and the rest. The funnel
       of the quiz is quiz_step by `step`. lp-posthog registers `lp: "quiz"` on every event, so it is not
       repeated here. Re-renders of the same screen (a tap, a toggle) send nothing. No PostHog on the page (the
       section disabled) sends nothing and costs nothing.
       OLE-94 (metric 4): each quiz_step also carries the order's record (QuizCore.cohortAttributes: every answer
       so far, the household, packs and frequency recommended and chosen, the purchase type), so any step can
       be broken down by any answer given before it. */
    var lastStep = null;
    /* quiz_screen_left (metric 11): once per screen, as the visitor leaves it for another screen or leaves the
       page. visible_ms counts only while the tab is showing; max_scroll_pct is how far down the screen the
       bottom of the viewport reached, which on the Box (the menu) is how far down the dishes they read. */
    var screen = null;
    function screenStart(key) {
      screen = { step: key, ms: 0, since: doc.visibilityState === 'hidden' ? null : Date.now(), max: 0 };
      sampleScroll();
      win.requestAnimationFrame(sampleScroll);             // after the new screen has laid out
    }
    function sampleScroll() {
      if (!screen) return;
      var de = doc.documentElement;
      var pct = scrollPct(win.scrollY || win.pageYOffset || 0, win.innerHeight || de.clientHeight, Math.max(de.scrollHeight, doc.body ? doc.body.scrollHeight : 0));
      if (pct > screen.max) screen.max = pct;
    }
    function screenLeave(toStep, reason) {
      if (!screen) return;
      sampleScroll();
      var ms = screen.ms + (screen.since == null ? 0 : Date.now() - screen.since);
      track('quiz_screen_left', { step: screen.step, to_step: toStep, reason: reason, visible_ms: Math.min(ms, 600000),
                                  max_scroll_pct: screen.max, packs: packsIn(S.box), after_basket: basketOk },
            reason === 'page_hide' ? BEACON : undefined);
      screen = null;
    }
    var scrollQueued = false;
    win.addEventListener('scroll', function () {
      if (scrollQueued) return;
      scrollQueued = true;
      win.requestAnimationFrame(function () { scrollQueued = false; sampleScroll(); });
    }, { passive: true });
    doc.addEventListener('visibilitychange', function () {
      if (!screen) return;
      if (doc.visibilityState === 'hidden') { if (screen.since != null) { screen.ms += Date.now() - screen.since; screen.since = null; } }
      else if (screen.since == null) screen.since = Date.now();
    });
    win.addEventListener('pagehide', function () { screenLeave(null, 'page_hide'); });
    /* back from /cart through the bfcache: the screen showing is a fresh view of it */
    win.addEventListener('pageshow', function (e) { if (e && e.persisted && lastStep) { basketOk = false; screenStart(lastStep); } });
    /* OLEO-197 (replays 01a105f0, 01a1013b): a PASS keeps the add's lock while the page leaves for /cart, and Back
       through the bfcache brought the page back with it held, so Add to order did nothing however often it was tapped.
       lp-quiz-cart.js frees its own lock the same way (its pageshow, `inFlight`); this frees the quiz's and the button. */
    win.addEventListener('pageshow', function (e) { if (e && e.persisted) { pending = null; busy(false); } });
    /* T1: back through the bfcache after a PASS closed the attempt: what follows is a new attempt */
    win.addEventListener('pageshow', function (e) { if (e && e.persisted && quizAttempt && quizAttempt.closed) startAttempt(false); });
    /* T1: a restart from scratch (lp-quiz.js resetDev: the R key on the review board, and its fallback when saved state
       will not render) starts a new attempt before the first screen is shown again */
    if (typeof win.resetDev === 'function') {
      var resetQuiz = win.resetDev;
      win.resetDev = function () { startAttempt(true); return resetQuiz.apply(this, arguments); };
    }
    function quizStep() {
      var key = (win.location.hash || '').slice(1);
      if (!ph() || !key || key === lastStep) return;
      /* OLEO-230: a dish screen held for Skio's list (lp-quiz.js holdForDishes) has not drawn yet, though a restored
         URL may already name it; its quiz_step goes when it draws. After every drawn screen the hash is that screen. */
      if (typeof lastKey !== 'undefined' && lastKey !== key) return;   // eslint-disable-line no-undef
      var flow = [];
      try { flow = visibleFlow(); } catch (e) {}
      screenLeave(key, 'screen_change');
      var props = { step: key, step_number: flow.indexOf(key) + 1, steps_total: flow.length,
                    from_step: lastStep, packs: packsIn(S.box), purchase: S.purchase };
      var rec = record();
      Object.keys(rec).forEach(function (k) { props[k] = rec[k]; });
      /* T3: the For you list as shown (the recommendation engine Tim wants to analyse), and the top of All */
      if (key === 'box') {
        try { var shown = boxShownProps(win.QuizCore, S, win.DISHES, pageFilter('foryou'), variant); Object.keys(shown).forEach(function (k) { props[k] = shown[k]; }); } catch (e) {}
        /* OLEO-147: how many dishes Skio's box list hid at load; null when the list was not applied */
        var nib = lpQuiz.notInBox;
        props.dishes_hidden_not_in_box = nib && nib.applied ? nib.hidden.length : null;
      }
      track('quiz_step', props);
      lastStep = key;
      screenStart(key);
    }
    if (typeof win.render === 'function') {
      var paint = win.render;
      win.render = function () {
        var out = paint.apply(this, arguments);
        try { quizStep(); } catch (e) {}
        if (pending) try { busy(true); } catch (e) {}    // OLEO-197: a redraw mid-add keeps the button busy
        return out;
      };
      win.render();                                        // the first paint ran before these were in place
    }
    /* eslint-enable no-undef */
  }

  return { researchKeys: researchKeys, NO_RESEARCH: NO_RESEARCH, uniqueListed: uniqueListed, buildDishes: buildDishes,
           packPrices: packPrices, orderFrom: orderFrom, boot: boot,
           routeOf: routeOf, scrollPct: scrollPct, packsIn: packsIn, basketProps: basketProps,
           basketResultProps: basketResultProps, dishChangeProps: dishChangeProps,
           ATTEMPT_KEY: ATTEMPT_KEY, ATTEMPT_GAP_MS: ATTEMPT_GAP_MS, newAttemptId: newAttemptId, nextAttempt: nextAttempt,
           restartOf: restartOf, shownLists: shownLists, boxShownProps: boxShownProps, positionIn: positionIn,
           answerDetail: answerDetail,
           SKIO_BOX: SKIO_BOX, skioBoxRequest: skioBoxRequest, skioSelectable: skioSelectable, notInBox: notInBox,
           EDIT_KEY: EDIT_KEY, answersFrom: answersFrom, editRestore: editRestore, applyEdit: applyEdit,
           POPUP_TRIGGERS: POPUP_TRIGGERS, POPUP_IDLE: POPUP_IDLE,
           wire: wire };                                   // exported for the tracking tests only
});
